import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  GetModelsQuery,
  GetModelsQueryVariables,
  Model,
  ModelType,
  ModelStatus,
  Engagements,
  DeleteModelMutation,
  DeleteModelMutationVariables,
} from '../../../../API';
import { GetModels } from '../../../../graphql/queries';
import { DeleteModel } from '../../../../graphql/mutations';
import { distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import { ModelWithUI } from '../../models/ml-data';
import { Pagination } from '../../../data-ingestion/models/data-ingestion';

@Component({
  selector: 'app-model-history',
  templateUrl: './model-history.component.html',
  styleUrl: './model-history.component.scss',
})
export class ModelHistoryComponent implements OnInit, OnDestroy {
  modelHistoryData: ModelWithUI[] = [];
  isLoading = false;
  searchValue = '';
  filteredData: ModelWithUI[] = [];
  engagement: string = '';
  lastModifiedByFilter: { text: string; value: string }[] = [];
  statusFilter: { text: string; value: string }[] = [
    { text: 'Success', value: ModelStatus.SUCCESS },
    { text: 'In Progress', value: ModelStatus.IN_PROGRESS },
    { text: 'Fail', value: ModelStatus.FAIL },
  ];
  headers: any[] = [];
  private subscription: Subscription = new Subscription();

  readonly pagination: Pagination = { page: 1, size: 10 };
  readonly supportedPageSizes = [10, 25, 50];
  totalCount?: number;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    public authService: AuthService,
    private apiService: ApiService,
    public appService: AppService,
    private nzMessageService: NzMessageService,
    private nzModalService: NzModalService,
  ) {}

  ngOnInit(): void {
    this.subscription = this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev.engagement !== curr.engagement),
      )
      .subscribe((engagements: Engagements) => {
        if (engagements?.engagement) {
          this.engagement = engagements.engagement;
          this.loadModelHistory();
        }
      });
    this.setHeaders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }

  loadModelHistory(): void {
    this.isLoading = true;

    this.apiService
      .query<GetModelsQuery, GetModelsQueryVariables>(GetModels, {
        engagementId: this.engagement,
      })
      .then((response) => {
        if (response.data?.GetModels) {
          this.modelHistoryData = this.mapModelsToModelHistory(response.data.GetModels);
          this.filteredData = [...this.modelHistoryData];
          this.setLastModifiedByFilter();
          this.getPageData();
        } else {
          this.modelHistoryData = [];
          this.filteredData = [];
          this.totalCount = 0;
        }
      })
      .catch((error) => {
        console.error('Error loading model history:', error);
        this.nzMessageService.error('Failed to load model history');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private mapModelsToModelHistory(models: Model[]): ModelWithUI[] {
    const baseModels = models.filter((model) => model.modelType === ModelType.BASE || !model.parentId);
    const lookalikeModels = models.filter((model) => model.modelType === ModelType.LOOKALIKE && model.parentId);

    return baseModels.map((baseModel) => {
      const childLookalikeModels = lookalikeModels.filter((la) => la.parentId === baseModel.modelId);

      return {
        ...baseModel,
        expand: false,
        lookalikeAudiences: childLookalikeModels,
      };
    });
  }

  setHeaders(): void {
    this.headers = [
      {
        name: '',
        width: '3rem',
      },
      {
        name: 'Model Name',
        sortOrder: null,
        sortFn: (a: ModelWithUI, b: ModelWithUI) =>
          (a.name || a.modelId || '').localeCompare(b.name || b.modelId || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '18rem',
      },
      {
        name: 'Model ID',
        width: '20rem',
      },
      {
        name: 'Seed Audience',
        sortOrder: null,
        sortFn: (a: ModelWithUI, b: ModelWithUI) =>
          (a.queryName || a.queryId || '').localeCompare(b.queryName || b.queryId || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '18rem',
      },
      {
        name: 'Seed Audience Size',
        sortFn: this.sortBySeedAudienceSize,
        sortDirections: ['ascend', 'descend', null],
        width: '10rem',
      },
      {
        name: 'Owner',
        key: 'owner',
        listOfFilter: this.lastModifiedByFilter,
        filterFn: this.ownerFilterFn,
        filterMultiple: true,
        sortOrder: null,
        sortFn: (a: ModelWithUI, b: ModelWithUI) => (a.createdBy || '').localeCompare(b.createdBy || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '12rem',
      },
      {
        name: 'Status',
        listOfFilter: this.statusFilter,
        filterFn: this.statusFilterFn,
        filterMultiple: true,
        sortOrder: null,
        sortFn: (a: ModelWithUI, b: ModelWithUI) => (a.status || '').localeCompare(b.status || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '8rem',
      },
      {
        name: 'Last Modified At',
        sortOrder: 'descend',
        sortFn: this.sortByLastModifiedAt,
        sortDirections: ['ascend', 'descend', null],
        width: '13rem',
      },
      {
        name: 'Action',
        width: '5rem',
        right: true,
      },
    ];
  }

  setLastModifiedByFilter(): void {
    this.lastModifiedByFilter = this.modelHistoryData
      .map((model) => model.createdBy)
      .filter((createdBy, index, arr) => createdBy && arr.indexOf(createdBy) === index)
      .map((createdBy) => {
        return {
          text: createdBy || '--',
          value: createdBy || '--',
        };
      });

    const ownerHeader = this.headers.find((head) => head.key === 'owner');
    if (ownerHeader) {
      ownerHeader.listOfFilter = this.lastModifiedByFilter;
    }
  }

  getPageData(): void {
    this.totalCount = this.filteredData?.length;
  }

  onSearch(): void {
    if (!this.searchValue.trim()) {
      this.filteredData = this.modelHistoryData.map((model) => ({
        ...model,
        expand: false,
        lookalikeAudiences: model.lookalikeAudiences || [],
      }));
    } else {
      const searchTerm = this.searchValue.toLowerCase();
      this.filteredData = this.modelHistoryData
        .map((model) => {
          const baseModelMatches =
            (model.name || '').toLowerCase().includes(searchTerm) ||
            (model.createdBy || '').toLowerCase().includes(searchTerm) ||
            (model.queryName || '').toLowerCase().includes(searchTerm);

          const matchingLookalikeAudiences = (model.lookalikeAudiences || []).filter(
            (lookalike) =>
              (lookalike.name || '').toLowerCase().includes(searchTerm) ||
              (lookalike.createdBy || '').toLowerCase().includes(searchTerm),
          );

          if (baseModelMatches || matchingLookalikeAudiences.length > 0) {
            return {
              ...model,
              expand: matchingLookalikeAudiences.length > 0 && !baseModelMatches,
              lookalikeAudiences:
                matchingLookalikeAudiences.length > 0 ? matchingLookalikeAudiences : model.lookalikeAudiences,
            };
          }

          return null;
        })
        .filter((model): model is ModelWithUI => model !== null);
    }
    this.getPageData();
  }

  getStatusColor(status: ModelStatus | null | undefined): string {
    if (!status) return 'default';

    switch (status) {
      case ModelStatus.SUCCESS:
        return 'success';
      case ModelStatus.IN_PROGRESS:
        return 'warning';
      case ModelStatus.FAIL:
        return 'error';
      default:
        return 'default';
    }
  }

  getStatusDisplayName(status: ModelStatus | null | undefined): string {
    switch (status) {
      case ModelStatus.SUCCESS:
        return 'Success';
      case ModelStatus.IN_PROGRESS:
        return 'In progress';
      case ModelStatus.FAIL:
        return 'Fail';
      default:
        return '--';
    }
  }

  sortByLastModifiedAt = (a: ModelWithUI, b: ModelWithUI): number => {
    const dateA = new Date(a.lastModifiedAt!).getTime();
    const dateB = new Date(b.lastModifiedAt!).getTime();
    return dateA - dateB;
  };

  sortBySeedAudienceSize = (a: ModelWithUI, b: ModelWithUI): number => {
    return (b.seedAudienceSize || 0) - (a.seedAudienceSize || 0);
  };

  ownerFilterFn = (ownerList: string[], model: ModelWithUI): boolean => {
    return ownerList.some((owner) => (model.createdBy || '--') === owner);
  };

  statusFilterFn = (statusList: string[], model: ModelWithUI): boolean => {
    return statusList.some((status) => model.status === status);
  };

  onModelAction(action: string, model: ModelWithUI): void {
    switch (action) {
      case 'view':
        this.router.navigate(['/ml/view-model', model.modelId]);
        break;
      case 'delete':
        this.confirmDeleteModel(model);
        break;
      default:
    }
  }

  onLookalikeAction(action: string, lookalike: Model): void {
    switch (action) {
      case 'view':
        this.router.navigate(['/ml/view-model', lookalike.modelId]);
        break;
      case 'delete':
        this.confirmDeleteLookalike(lookalike);
        break;
      default:
    }
  }

  formatNumber(num: number | null): string {
    if (!num) return '--';
    return new Intl.NumberFormat().format(num);
  }

  confirmDeleteLookalike(model: Model): void {
    this.nzModalService.confirm({
      nzTitle: 'Delete Lookalike',
      nzContent: `Are you sure you want to delete the lookalike ${model.name}? This action cannot be undone.`,
      nzOkText: 'Delete',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => this.deleteModel(model),
    });
  }

  confirmDeleteModel(model: ModelWithUI): void {
    this.nzModalService.confirm({
      nzTitle: 'Delete Model',
      nzContent: `Are you sure you want to delete the model ${model.name}? This action cannot be undone.`,
      nzOkText: 'Delete',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => this.deleteModel(model),
    });
  }

  deleteModel(model: ModelWithUI | Model): void {
    this.isLoading = true;
    const variables: DeleteModelMutationVariables = {
      input: {
        engagement: this.engagement,
        modelId: model.modelId,
        parentId: model.parentId,
      },
    };

    this.apiService
      .query<DeleteModelMutation, DeleteModelMutationVariables>(DeleteModel, variables)
      .then((response: any) => {
        if (response.data?.DeleteModel?.success) {
          this.nzMessageService.success(`Model ${model.name} deleted successfully from engagement ${this.engagement}`);
          this.loadModelHistory();
        } else {
          this.nzMessageService.error('Failed to delete model');
        }
      })
      .catch((error: any) => {
        console.error('Error deleting model:', error);
        this.nzMessageService.error('Failed to delete model');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  canDeleteModel(model: ModelWithUI): boolean {
    return (
      model.modelType === ModelType.BASE &&
      !model.lookalikeAudiences?.length &&
      model.status !== ModelStatus.IN_PROGRESS
    );
  }

  canDeleteLookalike(model: Model): boolean {
    return model.modelType === ModelType.LOOKALIKE && model.status !== ModelStatus.IN_PROGRESS;
  }
}
